import {
  USER_TYPE,
  getPermissionsForUserType,
  buildPermissions,
  getPermissionMetadata,
  createPermissionChecker,
  type AppConfigRoot,
  type EntityNames
} from '@a1/shared';
import { useSelector } from 'react-redux';
import { useGetEntityName } from './useGetEntity';

interface FeatureFlags {
  companies: boolean;
  [key: string]: boolean;
}

interface PermissionOptions {
  isExternalUser: boolean;
  featureFlags: FeatureFlags;
}

/**
 * Hook to get permissions for a specific user type
 * @param userType The type of user to get permissions for
 * @returns Built permissions based on user type
 */
const usePermissions = (userType: USER_TYPE): ReturnType<typeof buildPermissions> => {
  const appConfig = useSelector((state: AppConfigRoot) => state.appConfig?.data);
  const companiesFeatureEnabled = Boolean(appConfig?.others?.companies);
  const isExternalUser = userType === USER_TYPE.EXTERNAL_USER;

  // Get appropriate entity names
  const respondentEntityName = useGetEntityName('respondent');
  const respondentsEntityName = useGetEntityName('respondents');
  const managerEntityName = useGetEntityName('manager');
  const managersEntityName = useGetEntityName('managers');
  const extUserEntityName = useGetEntityName('externalUser');
  const bookingEntityName = useGetEntityName('booking');
  const bookingsEntityName = useGetEntityName('bookings');
  const bookingPageEntityName = useGetEntityName('bookingPage');
  const bookingPagesEntityName = useGetEntityName('bookingPages');
  const surveyEntityName = useGetEntityName('survey');
  const surveysEntityName = useGetEntityName('surveys');
  const surveyTypeEntityName = useGetEntityName('surveyType');
  const surveyTypesEntityName = useGetEntityName('surveyTypes');
  const adminsEntityName = useGetEntityName('admin');
  const externalUserEntityName = useGetEntityName('externalUser');
  const managerUserEntityName = useGetEntityName('manager');
  const adminUserEntityName = useGetEntityName('admin');

  let userEntityName: string;
  if (isExternalUser) {
    userEntityName = externalUserEntityName;
  } else if (userType === USER_TYPE.MANAGER) {
    userEntityName = managerUserEntityName;
  } else {
    userEntityName = adminUserEntityName;
  }

  const entityNames: EntityNames = {
    respondentEntityName,
    respondentsEntityName,
    managerEntityName,
    managersEntityName,
    extUserEntityName,
    bookingEntityName,
    bookingsEntityName,
    bookingPageEntityName,
    bookingPagesEntityName,
    surveyEntityName,
    surveysEntityName,
    surveyTypeEntityName,
    surveyTypesEntityName,
    adminsEntityName,
    userEntityName
  };

  // Get permissions for the user type
  const permissionKeys = Object.values(getPermissionsForUserType(userType));

  // Get metadata based on user type
  const permissionMetadata = getPermissionMetadata(userType);

  const options: PermissionOptions = {
    isExternalUser,
    featureFlags: {
      companies: companiesFeatureEnabled
    }
  };

  return buildPermissions(permissionKeys, permissionMetadata, entityNames, options);
};

/**
 * Hook to check permissions for the current user
 * @returns Permission checker with various permission check methods
 */
const useCheckPermission = () => {
  const user = useSelector((state: AppConfigRoot) => state.user.data);
  return createPermissionChecker(user);
};

export { usePermissions, useCheckPermission };
